/**
 * UserComment Component
 *
 * Renders the input section for posting a new comment or answer in the forum.
 * Handles user image, text input, and sending the comment or answer.
 *
 * Features:
 * - Displays user image and text input for comment
 * - Handles posting new comments and answers (subcomments)
 * - Integrates with Redux for state management
 * - Uses Material-UI for UI elements and CSS modules for styling
 * - Passes relevant props to child components for actions
 *
 * @param {Object} props - Component properties
 * @param {number} [props.idLesson] - Lesson ID associated with the comment
 * @param {number} [props.idCourse] - Course ID associated with the comment
 * @param {bool} [props.subcomment] - Indicates if posting an answer (subcomment)
 * @param {number} [props.idComment] - ID of the comment to answer (if subcomment)
 * @param {function} [props.showAnswers] - Function to refresh answers after posting
 * @returns {JSX.Element} Rendered UserComment component
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useDispatch } from 'react-redux';

import {
  getCommentsForum,
  postNewAnswer,
  postNewComment
} from 'store/lesson-actions';

import classes from './UserComment.module.css';

const UserComment = props => {
  const { t } = useTranslation(['forum']);
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state.userInfo);
  const [forumComment, setForumComment] = useState('');

  /**
   * Handles text change for comment input
   */
  const handleChange = event => {
    setForumComment(event.target.value);
  };

  /**
   * Handles sending a new comment or answer
   */
  const sendCommentHandler = () => {
    (async () => {
      if (props.subcomment) {
        const newAnswer = await postNewAnswer(
          props.idCourse,
          props.idLesson,
          forumComment,
          'es',
          props.idComment
        );
        if (newAnswer) {
          setForumComment('');
          props.showAnswers();
        }
      } else {
        const newPost = await postNewComment(
          props.idCourse,
          props.idLesson,
          forumComment,
          'es'
        );
        if (newPost) {
          setForumComment('');
          dispatch(
            getCommentsForum(props.idLesson, 'es', 50, 1, true)
          );
        }
      }
    })();
  };

  return (
    <>
      {/* User image section */}
      <figure className={classes.userImageContainer}>
        {userInfo.data.foto ? (
          <img
            className={classes.userImage}
            src={userInfo.data.foto}
            alt={userInfo.data.nombre}
          />
        ) : (
          <img
            className={classes.userImage}
            src="https://d1g6c01s9ssd7h.cloudfront.net/img/usuario12.png"
            alt={userInfo.data.nombre}
          />
        )}
      </figure>
      {/* Material-UI TextField for comment input */}
      <TextField
        label={t('write_comment')}
        multiline
        maxRows={4}
        value={forumComment}
        onChange={handleChange}
        className={classes.forumInput}
      />
      {/* Material-UI Button to send comment */}
      <Button
        className={classes.commentForumBtn}
        onClick={sendCommentHandler}
        disabled={!forumComment}
      >
        {t('comment')}
      </Button>
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
UserComment.propTypes = {
  /** Lesson ID associated with the comment */
  idLesson: PropTypes.number,
  /** Course ID associated with the comment */
  idCourse: PropTypes.number,
  /** Indicates if posting an answer (subcomment) */
  subcomment: PropTypes.bool,
  /** ID of the comment to answer (if subcomment) */
  idComment: PropTypes.number,
  /** Function to refresh answers after posting */
  showAnswers: PropTypes.func
};

UserComment.defaultProps = {
  idLesson: undefined,
  idCourse: undefined,
  subcomment: undefined,
  idComment: undefined,
  showAnswers: undefined
};

export default UserComment;
